import { useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LazyMotion, domAnimation } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { Layout } from "@/components/Layout";
import CookieConsent from "@/components/CookieConsent";
import { setMetaTag } from "@/utils/meta";
import ROUTE_META from "@/data/routeMeta.json";
import Home from "@/pages/Home";
import { gtagEvent, initScrollDepth, trackClick } from "@/analytics";

const About = lazy(() => import("@/pages/About"));
const Purtier = lazy(() => import("@/pages/Purtier"));
const Process = lazy(() => import("@/pages/Process"));
const Faq = lazy(() => import("@/pages/Faq"));
const Contact = lazy(() => import("@/pages/Contact"));
const Admin = lazy(() => import("@/pages/Admin"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Privacy = lazy(() => import("@/pages/Privacy"));

const PageLoader = () => (
  <div className="flex min-h-[60vh] items-center justify-center">
    <p className="font-serif text-xl font-light italic text-burgundy/60">Зареждане...</p>
  </div>
);

const useLenis = () => {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.4, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
};
const DEFAULT_META = {
  title: "PURTIER.BG – PURTIER седмо поколение | Лична консултация",
  description: "Личен консултант за PURTIER седмо поколение. Индивидуални консултации за клиенти от цяла България – по телефона, Viber, WhatsApp или онлайн. Запазете своята консултация.",
  ogTitle: "PURTIER.BG – PURTIER седмо поколение | Лична консултация",
  ogDescription: "Личен подход към PURTIER седмо поколение. Запазете консултация – по телефона, Viber, WhatsApp или онлайн.",
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    gtagEvent("page_view", { page_path: pathname });
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    const path = pathname === "/" ? "/" : pathname.replace(/\/$/, "");
    link.setAttribute("href", `https://purtier.bg${path}`);
    const meta = ROUTE_META[path] || DEFAULT_META;
    document.title = meta.title;
    setMetaTag('meta[name="description"]', "name", "description", meta.description);
    setMetaTag('meta[property="og:title"]', "property", "og:title", meta.ogTitle);
    setMetaTag('meta[property="og:description"]', "property", "og:description", meta.ogDescription);
  }, [pathname]);
  return null;
};

export const AppShell = ({ children }) => (
  <>
    <ScrollToTop />
    <Layout>
      <LazyMotion features={domAnimation} strict={false}>
        <Suspense fallback={<PageLoader />}>{children}</Suspense>
      </LazyMotion>
    </Layout>
    {typeof document !== "undefined" && (
      <Toaster position="bottom-right" toastOptions={{ style: { background: "#2C2A26", color: "#F5F1EA", borderRadius: 0, border: "none" } }} />
    )}
  </>
);

const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<Home />} />
    <Route path="/za-men" element={<About />} />
    <Route path="/purtier" element={<Purtier />} />
    <Route path="/priemat" element={<Process />} />
    <Route path="/faq" element={<Faq />} />
    <Route path="/kontakti" element={<Contact />} />
    <Route path="/admin" element={<Admin />} />
    <Route path="/blog" element={<Blog />} />
    <Route path="/blog/:slug" element={<BlogPost />} />
    <Route path="/poveritelnost" element={<Privacy />} />
    <Route path="*" element={<NotFound />} />
  </Routes>
);

function App() {
  useLenis();
  useEffect(() => {
    const onClick = (e) => trackClick(e.target);
    document.addEventListener("click", onClick);
    const cleanupScroll = initScrollDepth();
    return () => {
      document.removeEventListener("click", onClick);
      cleanupScroll();
    };
  }, []);
  return (
    <BrowserRouter>
      <AppShell>
        <AppRoutes />
      </AppShell>
    </BrowserRouter>
  );
}

export default App;
